import ExcelJS from "exceljs";
import {
  getDetailedRealEstateData,
  getDetailedDataStats,
  filterDetailedData
} from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/detailedRealEstateData";
import { getRealEstateData } from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/realEstateOwnersData";

/**
 * Export Real Estate Owners Complete Data to Excel
 * Includes: Dashboard Overview + Chart Data + 15-Minute Intervals
 */
export async function exportDetailedRealEstateToExcel(filters = {}) {
  try {
    const stats = getDetailedDataStats();
    const currentDate = new Date().toLocaleDateString("en-GB");
    const dashboardData = getRealEstateData();

    // Get filtered detailed data
    let dailyData;

    if (filters.day || filters.month || filters.year) {
      dailyData = filterDetailedData(filters);
    } else {
      const allMonthsData = getDetailedRealEstateData();
      dailyData = [];
      allMonthsData.forEach((month) => {
        dailyData.push(...month.dailyData);
      });
    }

    // Get monthly summary data (for charts)
    let monthlyChartData = dashboardData.monthlyData;
    if (filters.year) {
      monthlyChartData = monthlyChartData.filter((m) => {
        const [year] = m.month.split("-");
        return parseInt(year) === parseInt(filters.year);
      });
    }
    if (filters.month) {
      monthlyChartData = monthlyChartData.filter(
        (m) => m.month === filters.month
      );
    }

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "OneVR Analytics - Complete Export";
    workbook.created = new Date();
    workbook.modified = new Date();

    // ==================== SUMMARY SHEET ====================
    const summarySheet = workbook.addWorksheet("📊 Summary", {
      properties: { tabColor: { argb: "FF1E40AF" } }
    });
    createSummarySheet(summarySheet, stats, filters, dailyData);

    // ==================== DASHBOARD OVERVIEW ====================
    const overviewSheet = workbook.addWorksheet("📈 Dashboard Overview", {
      properties: { tabColor: { argb: "FF6366F1" } }
    });
    createDashboardOverviewSheet(overviewSheet, monthlyChartData);

    // ==================== MONTHLY CHART DATA ====================
    const monthlySheet = workbook.addWorksheet("📅 Monthly Data (Charts)", {
      properties: { tabColor: { argb: "FF8B5CF6" } }
    });
    createMonthlyChartDataSheet(monthlySheet, monthlyChartData);

    // ==================== 15-MIN INTERVAL SHEETS ====================

    // Energy Consumption Details
    const energySheet = workbook.addWorksheet("⚡ Energy (15-min)", {
      properties: { tabColor: { argb: "FFF59E0B" } }
    });
    createEnergyDetailsTable(energySheet, dailyData);

    // Carbon Emissions Details
    const carbonSheet = workbook.addWorksheet("🌱 Carbon (15-min)", {
      properties: { tabColor: { argb: "FFEF4444" } }
    });
    createCarbonDetailsTable(carbonSheet, dailyData);

    // ESG Score Details
    const esgSheet = workbook.addWorksheet("♻️ ESG (15-min)", {
      properties: { tabColor: { argb: "FF10B981" } }
    });
    createEsgDetailsTable(esgSheet, dailyData);

    // Occupancy Rate Details
    const occupancySheet = workbook.addWorksheet("👥 Occupancy (15-min)", {
      properties: { tabColor: { argb: "FF3B82F6" } }
    });
    createOccupancyDetailsTable(occupancySheet, dailyData);

    // Operational Cost Details
    const operationalSheet = workbook.addWorksheet("💰 Operational (15-min)", {
      properties: { tabColor: { argb: "FF8B5CF6" } }
    });
    createOperationalDetailsTable(operationalSheet, dailyData);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const filterSuffix = filters.day
      ? `Day-${filters.day}`
      : filters.month
      ? `Month-${filters.month}`
      : filters.year
      ? `Year-${filters.year}`
      : "Complete";
    link.download = `RealEstate-Analytics-${filterSuffix}-${currentDate.replace(
      /\//g,
      "-"
    )}.xlsx`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename: link.download };
  } catch (error) {
    console.error("Error exporting data to Excel:", error);
    return { success: false, error: error.message };
  }
}

// ==================== NEW: DASHBOARD OVERVIEW SHEET ====================

function createDashboardOverviewSheet(sheet, monthlyData) {
  sheet.columns = [{ width: 25 }, { width: 20 }, { width: 15 }, { width: 30 }];

  // Title
  sheet.mergeCells("A1:D1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "DASHBOARD OVERVIEW - KPI CARDS";
  titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6366F1" }
  };
  sheet.getRow(1).height = 35;

  const latestMonth = monthlyData[monthlyData.length - 1];

  let row = 3;

  // Energy Consumption Section
  addSectionHeader(sheet, row++, "⚡ ENERGY CONSUMPTION", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Energy Intensity",
    `${latestMonth.energy.intensity} kWh/sqm`,
    "Metric",
    "Current period consumption per square meter"
  );
  addOverviewRow(
    sheet,
    row++,
    "Change in Cost",
    `${latestMonth.energy.changePercent >= 0 ? "+" : ""}${
      latestMonth.energy.changePercent
    }%`,
    latestMonth.energy.changePercent >= 0 ? "Increase" : "Decrease",
    "Cost change compared to previous period"
  );
  addOverviewRow(
    sheet,
    row++,
    "Cost Prediction",
    `€${latestMonth.energy.nextMonthPrediction.toLocaleString()}`,
    "Next Month",
    "Predicted energy cost for next month"
  );
  addOverviewRow(
    sheet,
    row++,
    "Top Appliances Share",
    `${latestMonth.energy.topAppliancesShare}%`,
    "Consumption",
    "Top 3 appliances share of total energy"
  );

  row++;

  // Carbon Emissions Section
  addSectionHeader(sheet, row++, "🌱 CARBON EMISSIONS", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Total Emissions",
    `${latestMonth.carbon.totalEmissions} kg CO₂`,
    "Current",
    "Total carbon emissions for the period"
  );
  addOverviewRow(
    sheet,
    row++,
    "Gauge Value",
    `${latestMonth.carbon.gaugeValue}%`,
    "Performance",
    "Emissions performance indicator"
  );
  addOverviewRow(
    sheet,
    row++,
    "Floor 1 Share",
    `${latestMonth.carbon.floorBreakdown[0].percentage}%`,
    `Change: ${
      latestMonth.carbon.floorBreakdown[0].changePercent >= 0 ? "+" : ""
    }${latestMonth.carbon.floorBreakdown[0].changePercent}%`,
    "Floor 1 emissions percentage"
  );

  row++;

  // ESG Score Section
  addSectionHeader(sheet, row++, "♻️ ESG SCORE", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Overall Score",
    `${latestMonth.esg.overallScore}%`,
    "Current",
    "Overall ESG performance score"
  );
  addOverviewRow(
    sheet,
    row++,
    "CO₂ Reporting",
    latestMonth.esg.compliance.co2Reporting ? "✓ Active" : "✗ Inactive",
    "Compliance",
    "CO₂ reporting status"
  );
  addOverviewRow(
    sheet,
    row++,
    "Energy Tracking",
    latestMonth.esg.compliance.energyTracking ? "✓ Active" : "✗ Inactive",
    "Compliance",
    "Energy tracking status"
  );
  addOverviewRow(
    sheet,
    row++,
    "GRESB Integration",
    latestMonth.esg.compliance.gresbIntegration ? "✓ Active" : "✗ Inactive",
    "Compliance",
    "GRESB integration status"
  );

  row++;

  // Occupancy Rate Section
  addSectionHeader(sheet, row++, "👥 OCCUPANCY RATE", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Occupancy Rate",
    `${latestMonth.occupancy.rate}%`,
    "Current",
    "Current building occupancy percentage"
  );
  addOverviewRow(
    sheet,
    row++,
    "People Onsite",
    `${latestMonth.occupancy.peopleOnsite} people`,
    `Capacity: ${latestMonth.occupancy.totalCapacity}`,
    "Current number of people in building"
  );
  addOverviewRow(
    sheet,
    row++,
    "Space Utilization",
    `${latestMonth.occupancy.spaceUtilization}%`,
    "Efficiency",
    "Overall space utilization percentage"
  );

  row++;

  // Operational Cost Section
  addSectionHeader(sheet, row++, "💰 OPERATIONAL COST", "A", "D");
  addOverviewRow(
    sheet,
    row++,
    "Energy Analytics",
    `${latestMonth.operational.energyAnalytics}%`,
    "Performance",
    "Energy analytics efficiency"
  );
  addOverviewRow(
    sheet,
    row++,
    "Dynamic Occupancy",
    `${latestMonth.operational.dynamicOccupancy}%`,
    "Real-time",
    "Dynamic occupancy tracking"
  );
  addOverviewRow(
    sheet,
    row++,
    "Utilization Analytics",
    `${latestMonth.operational.utilizationAnalytics}%`,
    "Efficiency",
    "Space utilization analytics"
  );
}

function addOverviewRow(sheet, rowNum, label, value, category, description) {
  const row = sheet.getRow(rowNum);

  row.getCell(1).value = label;
  row.getCell(1).font = { bold: true, size: 11 };
  row.getCell(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  row.getCell(2).value = value;
  row.getCell(2).font = { size: 11, bold: true, color: { argb: "FF2563EB" } };
  row.getCell(2).alignment = { horizontal: "center" };

  row.getCell(3).value = category;
  row.getCell(3).font = { size: 10, italic: true };
  row.getCell(3).alignment = { horizontal: "center" };

  row.getCell(4).value = description;
  row.getCell(4).font = { size: 10 };

  row.height = 22;

  row.eachCell((cell) => {
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } }
    };
  });
}

// ==================== NEW: MONTHLY CHART DATA SHEET ====================

function createMonthlyChartDataSheet(sheet, monthlyData) {
  sheet.columns = [
    { width: 15 }, // Month
    { width: 12 }, // Energy Intensity
    { width: 12 }, // Total kWh
    { width: 12 }, // Total Cost
    { width: 12 }, // Carbon Emissions
    { width: 12 }, // ESG Score
    { width: 12 }, // Occupancy Rate
    { width: 12 }, // People Onsite
    { width: 15 }, // Energy Analytics
    { width: 15 } // Dynamic Occupancy
  ];

  const headers = [
    "Month",
    "Energy\nIntensity\n(kWh/sqm)",
    "Total\nEnergy\n(kWh)",
    "Total\nCost\n(€)",
    "Carbon\nEmissions\n(kg CO₂)",
    "ESG\nScore\n%",
    "Occupancy\nRate %",
    "People\nOnsite",
    "Energy\nAnalytics\n%",
    "Dynamic\nOccupancy\n%"
  ];

  createTableHeader(sheet, headers, "FF8B5CF6");

  monthlyData.forEach((month, index) => {
    const rowNum = index + 2;
    const isEven = rowNum % 2 === 0;

    styleDataCell(sheet.getCell(rowNum, 1), month.displayMonth, isEven);
    styleDataCell(
      sheet.getCell(rowNum, 2),
      month.energy.intensity,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 3),
      month.energy.totalKwh,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 4),
      month.energy.totalCost,
      isEven,
      "currency"
    );
    styleDataCell(
      sheet.getCell(rowNum, 5),
      month.carbon.totalEmissions,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 6),
      month.esg.overallScore,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 7),
      month.occupancy.rate,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 8),
      month.occupancy.peopleOnsite,
      isEven,
      "number"
    );
    styleDataCell(
      sheet.getCell(rowNum, 9),
      month.operational.energyAnalytics,
      isEven,
      "decimal"
    );
    styleDataCell(
      sheet.getCell(rowNum, 10),
      month.operational.dynamicOccupancy,
      isEven,
      "decimal"
    );

    sheet.getRow(rowNum).height = 20;
  });
}

// ==================== EXISTING HELPER FUNCTIONS (keep all previous helpers) ====================

function createTableHeader(sheet, headers, color) {
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: color } };
    cell.border = {
      top: { style: "medium", color: { argb: "FF000000" } },
      bottom: { style: "medium", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FFFFFFFF" } },
      right: { style: "thin", color: { argb: "FFFFFFFF" } }
    };
  });
  sheet.getRow(1).height = 40;
}

function styleDataCell(cell, value, isEven, format = "text") {
  cell.value = value;
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: isEven ? "FFFFFFFF" : "FFF9FAFB" }
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };

  if (format === "number") {
    cell.numFmt = "#,##0";
  } else if (format === "decimal") {
    cell.numFmt = "0.0";
  } else if (format === "currency") {
    cell.numFmt = "€#,##0.00";
  } else if (format === "percent") {
    cell.numFmt = '0.0"%"';
  } else if (format === "time") {
    cell.numFmt = "@";
  }
}

function createSummarySheet(sheet, stats, filters, dailyData) {
  sheet.columns = [{ width: 30 }, { width: 50 }];

  sheet.mergeCells("A1:B1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "REAL ESTATE ANALYTICS - COMPLETE EXPORT";
  titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1E40AF" }
  };
  sheet.getRow(1).height = 35;

  const totalIntervals = dailyData.reduce(
    (sum, day) => sum + day.bookings.length,
    0
  );

  let row = 3;
  addDataRow(
    sheet,
    row++,
    "Export Type",
    "Complete Analytics (Dashboard + 15-min Intervals)"
  );
  addDataRow(sheet, row++, "Total Days Exported", dailyData.length);
  addDataRow(
    sheet,
    row++,
    "Total 15-min Intervals",
    totalIntervals.toLocaleString()
  );
  addDataRow(sheet, row++, "Interval Duration", "15 minutes");
  addDataRow(sheet, row++, "Operating Hours", "08:00 - 19:00");

  row++;
  addSectionHeader(sheet, row++, "Applied Filters");
  addDataRow(sheet, row++, "Year", filters.year || "All Available");
  addDataRow(sheet, row++, "Month", filters.month || "All Available");
  addDataRow(sheet, row++, "Day", filters.day || "All Available");

  row++;
  addSectionHeader(sheet, row++, "Sheet Structure");
  addDataRow(sheet, row++, "📊 Summary", "This overview sheet");
  addDataRow(
    sheet,
    row++,
    "📈 Dashboard Overview",
    "KPI cards shown on dashboard"
  );
  addDataRow(
    sheet,
    row++,
    "📅 Monthly Data",
    "Chart-level monthly aggregations"
  );
  addDataRow(
    sheet,
    row++,
    "⚡ Energy (15-min)",
    "Detailed 15-minute energy intervals"
  );
  addDataRow(
    sheet,
    row++,
    "🌱 Carbon (15-min)",
    "Detailed 15-minute carbon intervals"
  );
  addDataRow(
    sheet,
    row++,
    "♻️ ESG (15-min)",
    "Detailed 15-minute ESG intervals"
  );
  addDataRow(
    sheet,
    row++,
    "👥 Occupancy (15-min)",
    "Detailed 15-minute occupancy intervals"
  );
  addDataRow(
    sheet,
    row++,
    "💰 Operational (15-min)",
    "Detailed 15-minute operational intervals"
  );

  row++;
  addDataRow(
    sheet,
    row++,
    "Report Generated",
    new Date().toLocaleDateString("en-GB")
  );
}

function addDataRow(sheet, rowNum, label, value) {
  sheet.getCell(`A${rowNum}`).value = label;
  sheet.getCell(`A${rowNum}`).font = {
    bold: true,
    size: 11,
    color: { argb: "FF374151" }
  };
  sheet.getCell(`A${rowNum}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  sheet.getCell(`B${rowNum}`).value = value;
  sheet.getCell(`B${rowNum}`).font = { size: 11, color: { argb: "FF1F2937" } };

  sheet.getRow(rowNum).height = 20;
}

function addSectionHeader(sheet, rowNum, text, startCol = "A", endCol = "B") {
  sheet.mergeCells(`${startCol}${rowNum}:${endCol}${rowNum}`);
  const cell = sheet.getCell(`${startCol}${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createEnergyDetailsTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 12 }, // Total kWh
    { width: 12 }, // Electricity kWh
    { width: 10 }, // Gas kWh
    { width: 10 } // Cost €
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Total Energy\n(kWh)",
    "Electricity\n(kWh)",
    "Gas\n(kWh)",
    "Cost\n(€)"
  ];

  createTableHeader(sheet, headers, "FFF59E0B");

  let rowNum = 2;
  dailyData.forEach((day) => {
    day.energyIntervals.forEach((interval) => {
      const isEven = rowNum % 2 === 0;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), interval.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        interval.totalKwh,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 5),
        interval.electricityKwh,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 6),
        interval.gasKwh,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 7),
        interval.cost,
        isEven,
        "currency"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createCarbonDetailsTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 15 }, // Emissions (calculated per interval)
    { width: 12 } // Rate
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Emissions\n(kg CO₂)",
    "Rate\n(kg/kWh)"
  ];

  createTableHeader(sheet, headers, "FFEF4444");

  let rowNum = 2;
  dailyData.forEach((day) => {
    const dailyEmissions = day.dailySummary.carbon.totalEmissions;
    const intervalsCount = day.bookings.length;

    day.bookings.forEach((booking) => {
      const isEven = rowNum % 2 === 0;

      // Calculate emissions per interval (distribute daily total)
      const intervalEmissions =
        Math.round((dailyEmissions / intervalsCount) * 10) / 10;
      const rate = (intervalEmissions / 100).toFixed(2);

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        intervalEmissions,
        isEven,
        "decimal"
      );
      styleDataCell(sheet.getCell(rowNum, 5), rate, isEven, "decimal");

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createEsgDetailsTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 12 }, // Overall Score (varies per interval)
    { width: 12 }, // CO2 Reporting
    { width: 12 }, // Energy Tracking
    { width: 14 }, // GRESB
    { width: 12 } // ISO14001
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "ESG Score\n%",
    "CO2\nReporting",
    "Energy\nTracking",
    "GRESB\nIntegration",
    "ISO14001"
  ];

  createTableHeader(sheet, headers, "FF10B981");

  let rowNum = 2;
  dailyData.forEach((day) => {
    const baseScore = day.dailySummary.esg.overallScore;

    day.bookings.forEach((booking, index) => {
      const isEven = rowNum % 2 === 0;

      // Add small variation per interval
      const intervalScore =
        Math.round((baseScore + (Math.random() - 0.5) * 2) * 10) / 10;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
      styleDataCell(sheet.getCell(rowNum, 4), intervalScore, isEven, "decimal");
      styleDataCell(
        sheet.getCell(rowNum, 5),
        day.dailySummary.esg.compliance.co2Reporting ? "YES" : "NO",
        isEven
      );
      styleDataCell(
        sheet.getCell(rowNum, 6),
        day.dailySummary.esg.compliance.energyTracking ? "YES" : "NO",
        isEven
      );
      styleDataCell(
        sheet.getCell(rowNum, 7),
        day.dailySummary.esg.compliance.gresbIntegration ? "YES" : "NO",
        isEven
      );
      styleDataCell(
        sheet.getCell(rowNum, 8),
        day.dailySummary.esg.compliance.iso14001 ? "YES" : "NO",
        isEven
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createOccupancyDetailsTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 12 }, // Occupancy Rate (varies per interval)
    { width: 12 }, // People Onsite (varies per interval)
    { width: 12 }, // Open Office %
    { width: 12 }, // Meeting Room %
    { width: 10 }, // Lobby %
    { width: 12 } // Conference %
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Occupancy\nRate %",
    "People\nOnsite",
    "Open\nOffice %",
    "Meeting\nRoom %",
    "Lobby\n%",
    "Conference\nRoom %"
  ];

  createTableHeader(sheet, headers, "FF3B82F6");

  let rowNum = 2;
  dailyData.forEach((day) => {
    day.bookings.forEach((booking) => {
      const isEven = rowNum % 2 === 0;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        booking.occupancyRate,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 5),
        booking.peopleOnsite,
        isEven,
        "number"
      );
      styleDataCell(
        sheet.getCell(rowNum, 6),
        booking.spaceUtilization.openOffice,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 7),
        booking.spaceUtilization.meetingRoom,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 8),
        booking.spaceUtilization.lobby,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 9),
        booking.spaceUtilization.conferenceRoom,
        isEven,
        "decimal"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}

function createOperationalDetailsTable(sheet, dailyData) {
  sheet.columns = [
    { width: 12 }, // Date
    { width: 12 }, // Day
    { width: 8 }, // Time
    { width: 15 }, // Energy Analytics (varies per interval)
    { width: 15 }, // Dynamic Occupancy (varies per interval)
    { width: 18 } // Utilization Analytics (varies per interval)
  ];

  const headers = [
    "Date",
    "Day of Week",
    "Time",
    "Energy\nAnalytics %",
    "Dynamic\nOccupancy %",
    "Utilization\nAnalytics %"
  ];

  createTableHeader(sheet, headers, "FF8B5CF6");

  let rowNum = 2;
  dailyData.forEach((day) => {
    const baseEnergy = day.dailySummary.operational.energyAnalytics;

    day.bookings.forEach((booking) => {
      const isEven = rowNum % 2 === 0;

      // Add variation per interval
      const intervalEnergy =
        Math.round((baseEnergy + (Math.random() - 0.5) * 5) * 10) / 10;
      const intervalOccupancy = booking.occupancyRate;
      const intervalUtilization =
        Math.round((intervalOccupancy + (Math.random() - 0.5) * 3) * 10) / 10;

      styleDataCell(sheet.getCell(rowNum, 1), day.date, isEven);
      styleDataCell(sheet.getCell(rowNum, 2), day.dayOfWeek, isEven);
      styleDataCell(sheet.getCell(rowNum, 3), booking.time, isEven, "time");
      styleDataCell(
        sheet.getCell(rowNum, 4),
        intervalEnergy,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 5),
        intervalOccupancy,
        isEven,
        "decimal"
      );
      styleDataCell(
        sheet.getCell(rowNum, 6),
        intervalUtilization,
        isEven,
        "decimal"
      );

      sheet.getRow(rowNum).height = 18;
      rowNum++;
    });
  });
}
